import {
    forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import {
    Modal, Skeleton, Spin, Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { IOrder } from '../../types/order';
import { useFetch } from '../../hooks/useFetch';

interface IGlsCreateOrderFormModalProps {
    onHide?: () => void;
}

export interface IGlsCreateOrderFormModalRef {
    hide: () => void;
    show: (order?: IOrder) => void;
}

const GlsCreateOrderFormModal = forwardRef(({ onHide }: IGlsCreateOrderFormModalProps, ref) => {
    const errorsFetch = useFetch();
    const sendFetch = useFetch();
    const [errors, setErrors] = useState<{ id: number; data: string; created_at: string; }[]>([]);
    const [order, setOrder] = useState<IOrder>();
    const opened = useRef(false);
    const { t } = useTranslation();

    const getErrors = async () => {
        const response = await errorsFetch.request({
            url: `/gls/orders/${order?.id}/errors`,
            method: 'get',
        });
        if (Array.isArray(response.data)) {
            setErrors(response.data);
        }
    };

    useEffect(() => {
        if (order) {
            opened.current = true;
            getErrors();
        } else if (typeof onHide === 'function' && opened.current) {
            onHide();
        }
    }, [order]);

    const hide = () => {
        setErrors([]);
        setOrder(undefined);
    };

    const show = setOrder;

    useImperativeHandle(ref, () => ({
        hide,
        show,
    }));

    const onSendClick = async () => {
        const response = await sendFetch.request({
            url: '/gls/orders/create',
            method: 'post',
            data: {
                order_id: order?.id,
            },
        });
        if (response.success) {
            hide();
        } else {
            getErrors();
        }
    };

    return (
        <Modal
            title={t('confirmAction')}
            open={order instanceof Object}
            onCancel={hide}
            onOk={onSendClick}
            okButtonProps={{ loading: sendFetch.loading }}
            okText={t('createGlsOrder')}
        >
            <Spin spinning={errorsFetch.loading && errors.length > 0}>
                <Skeleton loading={errorsFetch.loading && errors.length === 0}>
                    <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
                        {errors.map((error) => (
                            <Typography.Paragraph key={error.id}>
                                <Typography.Title level={5}>{error.created_at}</Typography.Title>
                                <Typography.Text>{error.data}</Typography.Text>
                            </Typography.Paragraph>
                        ))}
                    </div>
                </Skeleton>
            </Spin>
        </Modal>
    );
});

export default GlsCreateOrderFormModal;
